// Runtime map-provider configuration, surfaced through window._env_ by the code
// generator (env-config.js). Apps generated before Amap support have no MAP_PROVIDER,
// so every accessor here defaults to Google Maps — preserving existing behavior.
import { KuikaEnvConfig } from "./frontend-url";

export type MapProvider = "GoogleMaps" | "Amap";

interface GlobalWithEnv {
  _env_?: KuikaEnvConfig;
}

/** Amap runtime credentials resolved from the generated env-config. */
export interface AmapRuntimeConfig {
  key: string;
  securityJsCode: string;
  /** Amap World Map (世界地图): overseas tiles + multilingual labels. Requires the entitlement on the key. */
  worldMapEnabled: boolean;
}

function getEnv(): KuikaEnvConfig | undefined {
  return (globalThis as GlobalWithEnv)._env_;
}

/**
 * The selected map provider. Defaults to "GoogleMaps" when unset (older generated
 * apps, or projects that never opted into Amap).
 */
export function getMapProvider(): MapProvider {
  const provider: string | undefined = getEnv()?.MAP_PROVIDER;
  return provider === "Amap" ? "Amap" : "GoogleMaps";
}

/** True when the app should render maps and geocode via Amap instead of Google Maps. */
export function isAmapProvider(): boolean {
  const provider: MapProvider = getMapProvider();
  return provider === "Amap";
}

/**
 * The Google Maps JS API key surfaced from the generated config (window._env_), or an
 * empty string when unset. Components should prefer this over any hardcoded literal.
 */
export function getGoogleMapsKey(): string {
  return getEnv()?.GOOGLE_MAPS_KEY ?? "";
}

/**
 * Amap credentials for the JSAPI loader, or null when Amap is not the selected
 * provider or no key was configured.
 */
export function getAmapRuntimeConfig(): AmapRuntimeConfig | null {
  const isAmap: boolean = isAmapProvider();
  if (!isAmap) return null;

  const env: KuikaEnvConfig | undefined = getEnv();
  const key: string = env?.AMAP_KEY ?? "";
  if (!key) return null;

  return {
    key,
    securityJsCode: env?.AMAP_SECURITY_JS_CODE ?? "",
    worldMapEnabled: env?.AMAP_WORLD_MAP === "true"
  };
}

/** True when Amap is the provider and the project enabled the World Map service on its key. */
export function isAmapWorldMapEnabled(): boolean {
  const config: AmapRuntimeConfig | null = getAmapRuntimeConfig();
  return config?.worldMapEnabled === true;
}
